/**
 * Schedule Release Types for MEV-Med Frontend
 * Type definitions for the 3-step schedule release wizard
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para liberacao de agenda profissional
 */

/**
 * Holiday scope enum
 */
export type HolidayScope = 'NATIONAL' | 'STATE' | 'MUNICIPAL' | 'COMPANY';

/**
 * Holiday decision for the release wizard
 */
export interface HolidayDecision {
  /** Holiday ID */
  holidayId: number;
  /** Date of the holiday (YYYY-MM-DD) */
  date: string;
  /** Name of the holiday */
  name: string;
  /** Scope of the holiday */
  scope: HolidayScope;
  /** Whether to work on this holiday */
  workOnHoliday: boolean;
}

/**
 * Holiday info returned from preview
 */
export interface HolidayInfo {
  id: number;
  date: string;
  name: string;
  scope: HolidayScope;
  isRecurring: boolean;
}

/**
 * Step 1: Preview request data
 */
export interface ScheduleReleasePreviewRequest {
  professionalId: number;
  clinicId: number;
  startDate: string;
  endDate: string;
}

/**
 * Step 1: Preview response data
 */
export interface ScheduleReleasePreviewResponse {
  period: {
    startDate: string;
    endDate: string;
    totalDays: number;
  };
  holidays: HolidayInfo[];
  schedule: {
    professionalName: string;
    clinicName: string;
    workingDays: number[];
    slotDuration: number;
    totalWorkingDays: number;
  };
}

/**
 * Step 2: Summary request data
 */
export interface ScheduleReleaseSummaryRequest {
  professionalId: number;
  clinicId: number;
  startDate: string;
  endDate: string;
  holidayDecisions: HolidayDecision[];
}

/**
 * Step 2: Summary response data
 */
export interface ScheduleReleaseSummaryResponse {
  period: {
    startDate: string;
    endDate: string;
    totalDays: number;
  };
  totals: {
    totalDays: number;
    totalHours: number;
    totalSlots: number;
    holidaysSkipped: number;
    holidaysWorked: number;
  };
  breakdown: Array<{
    date: string;
    weekDay: number;
    weekDayName: string;
    slots: number;
    hours: number;
    isHoliday: boolean;
    holidayName?: string;
    workOnHoliday?: boolean;
  }>;
}

/**
 * Step 3: Confirm request data
 */
export interface ScheduleReleaseConfirmRequest {
  professionalId: number;
  clinicId: number;
  companyId?: number;
  startDate: string;
  endDate: string;
  holidayDecisions: HolidayDecision[];
}

/**
 * Step 3: Confirm response data
 */
export interface ScheduleReleaseConfirmResponse {
  message: string;
  release: ScheduleRelease;
  slotsCreated: number;
}

/**
 * Schedule Release entity
 */
export interface ScheduleRelease {
  id: number;
  professionalId: number;
  professionalName?: string;
  clinicId: number;
  clinicName?: string;
  companyId: number;
  companyName?: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  totalHours: number;
  totalSlots: number;
  holidaysSkipped: number;
  holidaysWorked: number;
  holidayDecisions: HolidayDecision[];
  releasedById: number;
  releasedByName?: string;
  releasedAt: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * History filters
 */
export interface ScheduleReleaseHistoryFilters {
  professionalId?: number;
  clinicId?: number;
  companyId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

/**
 * History response with pagination
 */
export interface ScheduleReleaseHistoryResponse {
  releases: ScheduleRelease[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Summary statistics
 */
export interface ScheduleReleaseSummaryStats {
  totalReleases: number;
  totalSlots: number;
  totalHours: number;
  lastReleaseDate: string | null;
  averageSlotsPerRelease: number;
}

/**
 * Wizard step state
 */
export interface ScheduleReleaseWizardState {
  step: 1 | 2 | 3;
  professionalId: number | null;
  clinicId: number | null;
  startDate: string;
  endDate: string;
  preview: ScheduleReleasePreviewResponse | null;
  holidayDecisions: HolidayDecision[];
  summary: ScheduleReleaseSummaryResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * Wizard props
 */
export interface ScheduleReleaseWizardProps {
  open: boolean;
  onClose: () => void;
  professionalId: number;
  professionalName: string;
  clinicId: number;
  clinicName: string;
  companyId: number;
  onComplete?: (release: ScheduleRelease, slotsCreated: number) => void;
}

/**
 * Helper function to format period display
 */
export function formatPeriod(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startFormatted = start.toLocaleDateString('pt-BR');
  const endFormatted = end.toLocaleDateString('pt-BR');
  return `${startFormatted} a ${endFormatted}`;
}

/**
 * Helper function to format hours display
 */
export function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) {
    return `${h}h`;
  }
  return `${h}h ${m}min`;
}

/**
 * Helper function to get default date range (next 4 weeks)
 */
export function getDefaultDateRange(): { startDate: string; endDate: string } {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() + 1); // Start from tomorrow

  const end = new Date(start);
  end.setDate(end.getDate() + 28); // 4 weeks

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}
